import React, { Component, Fragment, setGlobal } from "reactn";
import { Button, Divider, Icon, MenuItem, MenuList, Popover } from "@material-ui/core";
import { _ } from "js/vendor";
import styled from "styled-components";
import { ds } from "js/core/models/dataService";
import { FeatureType } from "js/core/models/features";
import { trackActivity } from "js/core/utilities/utilities";
import { PopupMenu } from "js/editor/elementUI/EditorComponents/PopupMenu";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog, ShowUpgradeDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap10 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import ProBadge from "js/react/components/ProBadge";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { UploadFontModal } from "js/react/views/ThemeEditor/components/Font/UploadFontModal";
import shouldShowProBadge from "js/editor/shouldShowProBadge";
import { NestedMenuItem } from "js/editor/elementUI/EditorComponents/NestedMenuItem";
import { fontManager } from "js/core/services/fonts";
import log from "js/core/logger";

const CurrentFontImage = styled.img`
    height: 24px;
    margin-top: 5px;
`;

class FontList extends Component {
    constructor(props) {
        super(props);

        this.state = {
            anchorEl: null,
            fontModalOpen: false
        };
        //ds.selection.presentation does not exist when shared team theme is opened from the presentation library
        setGlobal({
            isSharedTheme: this.props.isSharedTheme,
            workspaceId: this.props.orgId || "personal",
            presentation: ds.selection.presentation,
            presentationId: ds.selection.presentation ? ds.selection.presentation.id : null,
            activePane: "organize"
        });
    }

    handleClick = event => {
        this.setState({ anchorEl: event.currentTarget });
    };

    handleClose = () => {
        this.setState({ anchorEl: null });
    };

    openUploadModal = () => {
        this.handleClose();
        let props;
        if (this.global.isSharedTheme) {
            props = {
                isSharedTheme: this.global.isSharedTheme,
                workspace_id: this.global.workspaceId
            };
            trackActivity("CustomFonts", "ShowUploadDialog", null, null, props);
        } else if (this.global.presentation) {
            if (!app.user.features.isFeatureEnabled(FeatureType.CUSTOM_FONTS, this.global.presentation.getWorkspaceId())) {
                ShowUpgradeDialog({
                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                    workspaceId: this.global.presentation.getWorkspaceId(),
                    analytics: { cta: "CustomFont", ...this.global.presentation.getAnalytics() },
                });
                return;
            }
            //if not team theme add workspace id
            props = {
                presentation_id: this.global.presentationId,
                workspace_id: this.global.workspaceId
            };
            trackActivity("CustomFonts", "ShowUploadDialog", null, null, props);
        }

        //if team theme props should be workspace id and sharedTheme true
        ShowDialog(UploadFontModal, {
            onSetCustomFont: (fontAssetId, baseWeight) => {
                this.handleSetFontStyle(fontAssetId, baseWeight);
            }
        });
    };

    handleSetFontStyle = (fontId, weight) => {
        const { update, style } = this.props;

        update({
            [`font${style}FontId`]: fontId,
            [`font${style}Weight`]: weight,
            [`font${style}BoldWeight`]: null,
            [`font${style}LineHeight`]: 1.6,
            [`font${style}LetterSpacing`]: 0,
            [`font${style}TextTransform`]: "auto",
            [`font${style}Scaling`]: 100
        });

        this.handleClose();
    };

    handleDeleteCustomFont = fontId => {
        const { currentFontStyle, refreshFonts, orgId } = this.props;

        if (fontId === currentFontStyle.fontId) {
            ShowErrorDialog({
                title: "Can't delete current font",
                message: "You cannot delete currently used font, select another font as current theme font first"
            });
        } else {
            ShowConfirmationDialog({
                title: "Are you sure you want to delete this custom font?",
                acceptCallback: async () => {
                    try {
                        const userAsset = ds.userAssets.find({ id: fontId });
                        if (userAsset) {
                            ds.userAssets.remove([userAsset]);
                        }

                        const asset = await ds.assets.getAssetById(fontId, "font");
                        if (asset) {
                            ds.assets.remove([asset]);
                        }

                        // Clean up the font cache to let it get loaded into ds.assets when
                        // requested
                        delete fontManager.loadFontPromises[fontId];

                        refreshFonts(orgId)
                            .catch(err => log.error(err));
                    } catch (err) {
                        log.error(err);
                    }
                }
            });
        }
    }

    render() {
        const {
            fonts,
            classes,
            currentFontStyle
        } = this.props;
        const { anchorEl } = this.state;

        const open = Boolean(anchorEl);

        const hasCustomFontFeature = app.user.features.isFeatureEnabled(FeatureType.CUSTOM_FONTS, this.props.orgId || "personal");

        const customFonts = _.sortBy(fonts.filter(font => font.isCustomFont), "label");
        const builtInFonts = _.sortBy(fonts.filter(font => font.isBuiltInFont), "label");

        return (
            <div className={classes.parent}>
                <div
                    className={classes.fontDropdown}
                    onClick={this.handleClick}
                >
                    <div className={classes.fontOptions}>
                        <CurrentFontImage src={currentFontStyle.imageData}></CurrentFontImage>
                    </div>
                    <Icon className={classes.dropDownIcon}>
                        arrow_drop_down
                    </Icon>
                </div>
                <Popover
                    disableEnforceFocus
                    anchorEl={anchorEl}
                    open={open}
                    onClose={this.handleClose}
                >
                    <div style={{ maxHeight: 560, overflowY: "auto" }}>
                        {(customFonts.length > 0 && hasCustomFontFeature) && (
                            <div>
                                <label className={classes.fontListLabel}>
                                    Uploaded Fonts
                                </label>
                                <Gap10 />
                                {customFonts.map(font => (
                                    <NestedMenuItem contents={<img src={font.imageData} height={24} />}>
                                        {font.styles
                                            .filter(style => !style.italic)
                                            .map((fontStyle, index) => (
                                                <FlexBox fillWidth>
                                                    <MenuItem
                                                        key={index}
                                                        onClick={() => this.handleSetFontStyle(font.id, fontStyle.weight)}
                                                    >
                                                        <FlexBox fillWidth>
                                                            <img height={24} src={fontStyle.imageData} />
                                                        </FlexBox>
                                                    </MenuItem>
                                                    <PopupMenu icon="more_vert" style={{ paddingLeft: "9px", paddingRight: 0 }} iconStyle={{ fontSize: 18 }}>
                                                        <MenuList>
                                                            <MenuItem onClick={event => {
                                                                event.stopPropagation();
                                                                this.handleDeleteCustomFont(font.id);
                                                            }}>
                                                                Delete Font
                                                            </MenuItem>
                                                        </MenuList>
                                                    </PopupMenu>
                                                </FlexBox>
                                            ))}
                                    </NestedMenuItem>
                                ))}
                                <Gap10 />
                                <Divider />
                            </div>
                        )}
                        {builtInFonts.length > 0 && (
                            <Fragment>
                                <label className={classes.fontListLabel}>
                                    Built-in fonts
                                </label>
                                <Gap10 />
                            </Fragment>
                        )}
                        {
                            builtInFonts.map(font => (
                                <NestedMenuItem contents={<img src={font.imageData} height={24} />}>
                                    {font.styles
                                        .filter(style => !style.italic)
                                        .map((fontStyle, index) => (
                                            <MenuItem
                                                key={index}
                                                onClick={() => this.handleSetFontStyle(font.id, fontStyle.weight)}
                                            >
                                                <img
                                                    height={24}
                                                    src={fontStyle.imageData}
                                                />
                                            </MenuItem>
                                        ))}
                                </NestedMenuItem>
                            ))
                        }
                    </div>
                    <Button
                        classes={{ root: classes.uploadFont }}
                        fullWidth={true}
                        onClick={this.openUploadModal}
                    >
                        <FlexBox>
                            <Icon style={{ marginRight: 8 }} color="primary">
                                cloud_upload
                            </Icon>
                            <div
                                className={classes.uppercase}
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    letterSpacing: 0
                                }}
                            >
                                Upload Fonts
                            </div>
                            {
                                this.global.presentation && (
                                    <ProBadge
                                        beforeShowDialogCallback={this.handleClose}
                                        show={shouldShowProBadge(FeatureType.CUSTOM_FONTS, this.global.presentation.getWorkspaceId())}
                                        analytics={{ cta: "CustomFont", ...this.global.presentation.getAnalytics() }}
                                        workspaceId={this.global.presentation.getWorkspaceId()}
                                    />
                                )
                            }
                        </FlexBox>
                    </Button>
                </Popover>

            </div>
        );
    }
}

export default FontList;
